import React from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {
    borderRadius,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    listUnStyled,
    m,
    mb,
    mh,
    mr,
    mt,
    mv,
    onDesktop,
    p,
    pb,
    position,
    pr,
    pt,
    truncateMultiline,
    w100
} from "@pg-design/helpers-css";
import {BrandListIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {pluralize} from "@pg-mono/string-utils";

import ResponsiveCard from "../../../atoms/responsive_card/ResponsiveCard";
import {AuthorBoxImgPlaceholder} from "./AuthorBoxImgPlaceholder";

interface IProps {
    author: {
        id: number;
        name: string;
        description: string;
        slug: string;
        image?: {
            a_img_130x130: string;
            a_img_360x360: string;
        };
        stats: {
            articles: number;
            categories: string[];
        };
    };
}

export const AuthorsListAuthorBox = (props: IProps) => {
    const theme = useTheme();
    const {id, name, description, image, stats, slug} = props.author;
    const categories = !!stats ? stats.categories.slice(0, 3) : null;
    const link = rpAppLink.article.author.detail({authorId: id, authorSlug: slug});

    return (
        <a css={[articleListAnchor]} href={link}>
            <ResponsiveCard
                cover={
                    <div css={[coverWrapperStyle]}>
                        {image ? <ResponsiveCard.ImgCover alt={name} src={image.a_img_360x360} /> : <AuthorBoxImgPlaceholder />}
                    </div>
                }
            >
                <div css={[position("relative")]}>
                    <div css={authorDetails}>
                        <Text variant="headline_4" as="h2">
                            {name}
                        </Text>
                        {!!categories && (
                            <ul css={[categoriesListStyle]}>
                                {categories.map((category) => {
                                    return (
                                        <Text key={category} variant="info_txt_1" as="li" css={[categoriesListElementStyle]} color={theme.colors.gray[700]}>
                                            {category}
                                        </Text>
                                    );
                                })}
                            </ul>
                        )}
                        <Text variant="body_copy_2" as="p" css={[authorDescriptionStyle]}>
                            {description}
                        </Text>
                        {!!stats && (
                            <Text variant="body_copy_2" css={[flex("center")]} as="div">
                                <BrandListIcon wrapperType="circle" wrapperColor={theme.colors.primary} wrapperSize="3" size="2.5" css={[mr(1)]} />{" "}
                                {stats.articles} {articlePluralize(stats.articles)}
                            </Text>
                        )}
                    </div>
                </div>
            </ResponsiveCard>
        </a>
    );
};

const articlePluralize = pluralize(["wpis", "wpisy", "wpisów"]);

const articleListAnchor = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    ${borderRadius()};
    ${elevation()};
    ${mv(1)};
    ${mh("auto")};
    background: #fff;
    overflow: hidden;
    max-width: 33rem;
    width: 100%;

    ${onDesktop(css`
        ${mv(1.5)};
        max-width: 100%;
    `)};

    &:visited,
    &:hover,
    &:focus,
    &:active {
        text-decoration: none;
        color: ${theme.colors.secondary};
    }
`;

const authorDetails = css`
    ${p(2)};
    max-width: 33rem;

    ${onDesktop(css`
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        max-width: 100%;
    `)};
`;

const categoriesListStyle = css`
    ${listUnStyled};
    ${m(0)};
    ${pt(1)};
    ${pb(1)};
    ${w100};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const categoriesListElementStyle = css`
    ${pr(2)};
    display: inline-block;
`;

const authorDescriptionStyle = css`
    ${mt(1)};
    ${mb(2)};
    ${truncateMultiline(4)};
`;

const coverWrapperStyle = css`
    ${flexAbsoluteCenter};

    ${onDesktop(css`
        width: 232px;
        height: 232px;
    `)};
`;
